import { v } from 'convex/values'

import { addDaysKey } from './goalFreeze'
import { dueOn } from './schedule'

// Productivity level: a 0–100 score for a window, measured against what was
// planned for it.
//
//   base  = Σ wᵢ·rᵢ / Σ wᵢ over the parts that had a plan
//     habits  w .45  r = scheduled check-ins hit / scheduled
//     tasks   w .40  r = min(1, effort of planned tasks done / effort planned)
//                        effort: super urgent 3, urgent 2, normal 1
//     goals   w .15  r = min(1, goal progress gained / goal target)
//                        target = min(GOAL_CAP_WEEK · days/7, gain the planned goal tasks offer)
//   bonus = min(BONUS_MAX, effort of unplanned tasks done + ½ per unscheduled check-in)
//   score = min(100, round(100·base + bonus)); null when nothing was planned
//
// Rest days (breathers, streak freezes), frozen-goal days and skips with a
// reason drop out of every denominator, and so does whatever is still open
// today unless the window is today itself.

export const WEIGHTS = { habits: 0.45, tasks: 0.4, goals: 0.15 } as const
export const BONUS_MAX = 5
export const GOAL_CAP_WEEK = 10
export const HABIT_EXTRA = 0.5
export const LEGENDARY_MIN = 95

export const LEVELS = [
  { id: 'warmingUp', min: 0 },
  { id: 'steady', min: 25 },
  { id: 'productive', min: 45 },
  { id: 'veryProductive', min: 60 },
  { id: 'extremelyProductive', min: 75 },
  { id: 'insane', min: 88 },
  { id: 'legendary', min: LEGENDARY_MIN },
] as const
export type LevelId = (typeof LEVELS)[number]['id']
export const LEVEL_IDS = LEVELS.map((l) => l.id) as LevelId[]

export const levelIdValidator = v.union(
  v.literal('warmingUp'),
  v.literal('steady'),
  v.literal('productive'),
  v.literal('veryProductive'),
  v.literal('extremelyProductive'),
  v.literal('insane'),
  v.literal('legendary'),
)

export const effortWeight = (t: { effort: string; superUrgent?: boolean | null }) =>
  t.effort !== 'high' ? 1 : t.superUrgent ? 3 : 2

export type ProdHabit = {
  period: string
  recurrenceDay?: number | null
  recursDays?: readonly number[] | null
  oneOff?: boolean
  startKey: string
  done: ReadonlySet<string>
  // Skips with a reason, frozen-goal and fast rest days.
  excused: ReadonlySet<string>
}

export type ProdTask = {
  weight: number
  // Goal progress points one completion adds (0 for inbox and recurring tasks).
  goalShare: number
  recurring?: boolean
  // Days it was surfaced on Today, committed to (must do) or due.
  planned: ReadonlySet<string>
  done: ReadonlySet<string>
  excused: ReadonlySet<string>
}

export type ProdInput = {
  today: string
  // Breather and streak-freeze days.
  rest: ReadonlySet<string>
  habits: readonly ProdHabit[]
  tasks: readonly ProdTask[]
}

export type Tally = {
  days: number
  habitsDue: number
  habitsHit: number
  habitsExtra: number
  taskPlanned: number
  taskDone: number
  taskExtra: number
  tasksDone: number
  urgentDone: number
  goalGain: number
  goalPotential: number
}

const PER_PERIOD = new Set(['weekly', 'monthly'])

function keysOf(from: string, to: string) {
  const out: string[] = []
  for (let k = from; k <= to && out.length < 400; k = addDaysKey(k, 1)) out.push(k)
  return out
}

// `live` counts what's still open today (the Today view); otherwise today only
// adds what's already done.
export function tally(input: ProdInput, from: string, to: string, live = false): Tally {
  const end = to < input.today ? to : input.today
  const keys = keysOf(from, end)
  const { rest, today } = input
  const counts = (k: string, done: boolean) => done || live || k !== today
  const t: Tally = {
    days: Math.max(1, keys.filter((k) => !rest.has(k)).length),
    habitsDue: 0,
    habitsHit: 0,
    habitsExtra: 0,
    taskPlanned: 0,
    taskDone: 0,
    taskExtra: 0,
    tasksDone: 0,
    urgentDone: 0,
    goalGain: 0,
    goalPotential: 0,
  }

  for (const h of input.habits) {
    const off = (k: string) => rest.has(k) || h.excused.has(k)
    const scheduled = (k: string) =>
      k >= h.startKey && !h.oneOff && dueOn(h.period, h.recurrenceDay, k, h.recursDays)
    if (PER_PERIOD.has(h.period)) {
      const n = keys.filter((k) => h.done.has(k)).length
      if (n > 0) {
        t.habitsDue++
        t.habitsHit++
        t.habitsExtra += n - 1
      } else if (keys.some((k) => scheduled(k) && !off(k) && counts(k, false))) t.habitsDue++
      continue
    }
    // Every-other-day: the day after a done or excused day is a rest day.
    const restAfter = (k: string) => {
      const prev = addDaysKey(k, -1)
      return prev < h.startKey || h.done.has(prev) || off(prev)
    }
    for (const k of keys) {
      const done = h.done.has(k)
      const due = scheduled(k) && !(h.period === 'everyOtherDay' && restAfter(k))
      if (!due) {
        if (done) t.habitsExtra++
      } else if (done) {
        t.habitsDue++
        t.habitsHit++
      } else if (!off(k) && counts(k, false)) t.habitsDue++
    }
  }

  const inWindow = (k: string) => k >= from && k <= end
  for (const task of input.tasks) {
    const off = (k: string) => rest.has(k) || task.excused.has(k)
    const credit = (done: boolean, planned: boolean) => {
      if (done) {
        t.tasksDone++
        if (task.weight >= 2) t.urgentDone++
        t.goalGain += task.goalShare
      }
      if (planned) {
        t.taskPlanned += task.weight
        t.goalPotential += task.goalShare
        if (done) t.taskDone += task.weight
      } else if (done) t.taskExtra += task.weight
    }
    if (task.recurring) {
      for (const k of keys) {
        const done = task.done.has(k)
        const planned = task.planned.has(k) && !off(k) && counts(k, done)
        if (done || planned) credit(done, planned)
      }
      continue
    }
    const first = [...task.done].sort()[0]
    const done = first !== undefined && inWindow(first)
    const planned = keys.some(
      (k) =>
        task.planned.has(k) && (first === undefined || k <= first) && !off(k) && counts(k, done),
    )
    if (done || planned) credit(done, planned)
  }
  return t
}

export function goalTarget(t: Tally) {
  return Math.min((GOAL_CAP_WEEK * t.days) / 7, t.goalPotential)
}

export function scoreOf(t: Tally): number | null {
  const parts: [number, number][] = []
  if (t.habitsDue > 0) parts.push([WEIGHTS.habits, t.habitsHit / t.habitsDue])
  if (t.taskPlanned > 0) parts.push([WEIGHTS.tasks, Math.min(1, t.taskDone / t.taskPlanned)])
  const target = goalTarget(t)
  if (target > 0) parts.push([WEIGHTS.goals, Math.min(1, t.goalGain / target)])
  if (!parts.length) return null
  const base = parts.reduce((s, [w, r]) => s + w * r, 0) / parts.reduce((s, [w]) => s + w, 0)
  const bonus = Math.min(BONUS_MAX, t.taskExtra + HABIT_EXTRA * t.habitsExtra)
  return Math.min(100, Math.round(base * 100 + bonus))
}

export const scoreWindow = (input: ProdInput, from: string, to: string, live = false) => {
  const t = tally(input, from, to, live)
  return { tally: t, score: scoreOf(t) }
}

const rawLevel = (score: number) => {
  let i = 0
  while (i + 1 < LEVELS.length && score >= LEVELS[i + 1].min) i++
  return i
}

// Legendary needs 95+ in the window before too (two weeks running); otherwise
// it holds at Insane with the gap to Legendary as 0.
export function levelFor(score: number, prevScore: number | null | undefined) {
  const legendary = LEVELS.length - 1
  let i = rawLevel(score)
  const locked = i === legendary && (prevScore ?? 0) < LEGENDARY_MIN
  if (locked) i = legendary - 1
  const next = i < legendary ? LEVELS[i + 1] : null
  return {
    level: LEVELS[i].id as LevelId,
    next: next
      ? { level: next.id as LevelId, points: Math.max(0, next.min - score) }
      : (null as { level: LevelId; points: number } | null),
    legendaryLocked: locked,
  }
}

export const mondayOf = (key: string) =>
  addDaysKey(key, -((new Date(`${key}T12:00:00Z`).getUTCDay() + 6) % 7))

// Best of the weeks scored so far (oldest first, current last), or the most
// recent earlier week that matched or beat it, when that was 3+ weeks back.
export function bestWeek(weeks: readonly { weekStart: string; score: number | null }[]) {
  const cur = weeks[weeks.length - 1]
  const past = weeks.slice(0, -1).filter((w) => w.score !== null)
  if (!cur || cur.score === null || !past.length)
    return { best: false, compared: 0, since: null as string | null }
  const higher = past.filter((w) => w.score! >= cur.score!)
  if (!higher.length && cur.score > 0)
    return { best: true, compared: past.length + 1, since: null as string | null }
  const last = higher[higher.length - 1]
  const back = last ? weeks.length - 1 - weeks.indexOf(last) : 0
  return { best: false, compared: 0, since: back >= 3 ? last.weekStart : null }
}

// Level colours on dark and light grounds (app accents from Productive up).
export const LEVEL_COLORS: Record<LevelId, { dark: string; light: string }> = {
  warmingUp: { dark: '#71717a', light: '#71717a' },
  steady: { dark: '#a1a1aa', light: '#52525b' },
  productive: { dark: '#d4a72c', light: '#a16207' },
  veryProductive: { dark: '#f59e0b', light: '#b45309' },
  extremelyProductive: { dark: '#fb923c', light: '#ea580c' },
  insane: { dark: '#ec6489', light: '#c2335d' },
  legendary: { dark: '#b87aff', light: '#7c3aed' },
}

export const levelRange = (l: LevelId) => {
  const i = LEVEL_IDS.indexOf(l)
  return [LEVELS[i].min, i + 1 < LEVELS.length ? LEVELS[i + 1].min - 1 : 100] as const
}

type HighlightDay = {
  date: string
  score: number | null
  level: LevelId | null
  rest: boolean
  future: boolean
  tasksDone: number
  habitsAll: boolean
}

// The week view's two lines: the best scored day, then a rest day (rest days
// never count against you) or else the lowest day when it dipped a level.
export function weekHighlights<D extends HighlightDay>(days: readonly D[]) {
  const scored = days.filter((d) => !d.future && d.score !== null)
  const best = scored.reduce<D | null>((b, d) => (!b || d.score! > b.score! ? d : b), null)
  const rest = days.find((d) => d.rest && !d.future)
  const low = scored.reduce<D | null>((b, d) => (!b || d.score! < b.score! ? d : b), null)
  return {
    best,
    rest: rest ?? null,
    dip: !rest && low && best && low.level !== best.level ? low : null,
  }
}
